import { toBoolean } from '@mnd-frontend/ts';
import type { BadgeVariant } from '@mnd-frontend/ui';
import { useTranslation } from 'react-i18next';
import type { MenuDataQuery } from '../../../../generated/types';
import routes from '../../../../routes';
import { type ItemGroupWithHeading, MenuState } from './types';

interface MainMenuItemGroup extends ItemGroupWithHeading {
  menuState: MenuState;
}

const useMainMenu = (data?: MenuDataQuery): MainMenuItemGroup[] => {
  const [t] = useTranslation();
  const pressroomId = data?.pressroom?.id;

  const monitorEnabled = data?.pressroom?.features.monitor.enabled;
  const hasSearchProfiles = data?.pressroom?.monitor?.hasSearchProfiles;
  const hasSmartSearchProfiles = data?.pressroom?.monitor?.hasSmartSearchProfiles;

  const showPitch = data?.pressroom?.features.pitch;

  const newAudienceBuilderAccess = data?.pressroom?.features.mndReach;
  const showAudienceBuilder =
    data?.pressroom?.features.audienceBuilder2.enabled ||
    data?.pressroom?.features.audienceBuilder2.showUpsellPage ||
    newAudienceBuilderAccess;

  const monitor: MainMenuItemGroup = {
    heading: 'Monitor',
    menuState: MenuState.Monitor,
    items:
      data?.pressroom && pressroomId && monitorEnabled && hasSearchProfiles
        ? [
            {
              url: routes.monitorInsights({ pressroomId }),
              text: t('monitor_navigation_insights'),
              activeUrl: `${routes.monitorInsights.pattern}/*`,
            },
            {
              url: routes.monitorSearchProfiles({ pressroomId }),
              text: t('menu_monitor_search_profiles'),
              activeUrl: `${routes.monitorSearchProfiles.pattern}/*`,
            },
            {
              url: routes.monitorNewsAlertsList({ pressroomId }),
              text: t('menu_monitor_news_alerts'),
              activeUrl: `${routes.monitorNewsAlertsList.pattern}/*`,
            },
            {
              url: routes.monitorClipboardList({ pressroomId }),
              text: t('menu_monitor_clipbooks'),
              activeUrl: `${routes.monitorClipboardList.pattern}/*`,
            },
          ].filter(toBoolean)
        : data?.pressroom && pressroomId && (!monitorEnabled || !hasSmartSearchProfiles)
          ? [{ url: routes.monitor({ pressroomId }), text: 'Monitor' }]
          : [],
  };

  if (data?.me?.user?.isOnlyJournalist) {
    return [];
  }

  const reportUpsellPage =
    data?.source?.features.brandReport?.enabled === false &&
    data?.source?.features.coverageReports.enabled === false &&
    data?.source?.features.brandReport.previousTrial === false;

  return [
    {
      heading: 'Publish',
      menuState: MenuState.Publish,
      items:
        data?.pressroom && data?.source && pressroomId
          ? [
              {
                url: routes.publishList({
                  pressroomId,
                  materialType: 'pressreleases',
                }),
                activeUrl: `${routes.publishList({
                  pressroomId,
                  materialType: 'pressreleases',
                })}/*`,
                text: t('common.pressreleases'),
              },
              {
                url: routes.publishList({
                  pressroomId,
                  materialType: 'news',
                }),
                activeUrl: `${routes.publishList({
                  pressroomId,
                  materialType: 'news',
                })}/*`,
                text: t('common.news'),
              },
              {
                url: routes.publishList({
                  pressroomId,
                  materialType: 'blog_posts',
                }),
                activeUrl: `${routes.publishList({
                  pressroomId,
                  materialType: 'blog_posts',
                })}/*`,
                text: t('common.blog_posts'),
              },
              {
                url: routes.publishList({
                  pressroomId,
                  materialType: 'events',
                }),
                activeUrl: `${routes.publishList({
                  pressroomId,
                  materialType: 'events',
                })}/*`,
                text: t('common.events'),
              },
              {
                url: routes.publishList({
                  pressroomId,
                  materialType: data.pressroom.features.images ? 'images' : 'videos',
                }),
                activeUrl: [
                  `${routes.publishImagesList({
                    pressroomId,
                  })}/*`,
                  `${routes.publishList({
                    pressroomId,
                    materialType: 'videos',
                  })}/*`,
                  `${routes.publishList({
                    pressroomId,
                    materialType: 'documents',
                  })}/*`,
                ],
                text: t('common_media'),
              },
              showPitch && {
                url: routes.pitchList({ pressroomId }),
                text: t('pitch_button'),
              },
            ].filter(toBoolean)
          : [],
    },
    {
      heading: 'Contacts',
      menuState: MenuState.Contacts,
      items:
        data?.pressroom && pressroomId
          ? [
              {
                url: routes.contacts({ pressroomId }),
                activeUrl: [
                  routes.contacts.pattern,
                  routes.contactListShow.pattern,
                  routes.contactsAll.pattern,
                  routes.contactsInactive.pattern,
                  routes.contactShow.pattern,
                ],
                exactMatch: true,
                text: t('menu_contacts_contact_lists'),
              },
              showAudienceBuilder && {
                url: newAudienceBuilderAccess
                  ? routes.contactAudienceBuilderMndReach({ pressroomId })
                  : routes.contactAudienceBuilder({ pressroomId, tab: 'advanced' }),
                activeUrl: [
                  routes.contactAudienceBuilderMndReach.pattern,
                  routes.contactAudienceBuilder.pattern,
                  '/:pressroomId/unlocker/audience_builder',
                ],
                text: t('menu_contacts_audience_builder'),
                ...(newAudienceBuilderAccess
                  ? {
                      badge: {
                        text: t('common_new'),
                        variant: 'commercial' as BadgeVariant,
                      },
                    }
                  : {}),
              },
              {
                url: routes.contactImport({ pressroomId }),
                text: t('menu_contacts_import'),
                activeUrl: [routes.contactImport.pattern, routes.contactImportTab.pattern],
              },
            ].filter(toBoolean)
          : [],
    },
    {
      heading: 'Analyze',
      menuState: MenuState.Analyze,
      items:
        data?.pressroom && pressroomId
          ? data?.pressroom?.features.analyzeLight.enabled ||
            data?.pressroom?.features.analyze.enabled
            ? [
                data?.pressroom?.features.analyzeLight.enabled && {
                  url: routes.analyzeOverview({
                    pressroomId,
                  }),
                  text: t('menu_analyze_reports_and_insights'),
                },
                data?.pressroom?.features.analyze.enabled && {
                  url: routes.analyzeNewsroom({
                    pressroomId,
                  }),
                  activeUrl: routes.analyzeNewsroom.pattern,
                  exactMatch: true,
                  text: t('menu_analyze_newsroom'),
                },
                data?.pressroom?.features.analyze.enabled && {
                  url: routes.analyzeMaterialList({
                    pressroomId,
                  }),
                  activeUrl: `${routes.analyzeMaterialList({
                    pressroomId,
                  })}/*`,
                  text: t('menu_analyze_materials'),
                },
              ].filter(toBoolean)
            : [{ text: 'unlocker', url: routes.analyze({ pressroomId }) }]
          : [],
    },
    monitor,
    {
      heading: 'Reports',
      menuState: MenuState.Reports,
      items:
        data?.pressroom && pressroomId
          ? reportUpsellPage
            ? [
                {
                  url: routes.reportsUpsell({ pressroomId }),
                  activeUrl: [routes.reports.pattern],
                  text: 'Reports',
                },
              ]
            : [
                {
                  url: routes.reports({ pressroomId }),
                  activeUrl: [
                    routes.reports.pattern,
                    routes.reportsEdit.pattern,
                    routes.reportsSlideshow.pattern,
                    routes.reportsPreview.pattern,
                    routes.reportsCreateBrand.pattern,
                    routes.reportsPreviewSection.pattern,
                  ],
                  exactMatch: true,
                  text: 'Reports',
                },
              ]
          : [],
    },
  ].filter(toBoolean);
};

export default useMainMenu;
